import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { StateUtils, StateWithProcess } from '@spartacus/core';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { UserIdService } from '../../auth/user-auth/facade';
import {
  B2BUserRole,
  Credentials,
  CustomerType,
  LoaderError,
  NameInputCheckResult,
  User,
  UserInputCheck,
  UserSignUp,
} from '../../model';
import {
  getProcessErrorFactory,
  getProcessLoadingFactory,
  getProcessStateFactory,
  getProcessSuccessFactory,
} from '../../process/store/selectors/process.selectors';
import { UserProfileConnector } from '../connectors/user-profile.connector';
import { UserFacade } from '../facades';
import { UserActions } from '../store/actions/index';
import { ResetPasswordSelectors, UserDetailsSelectors } from '../store/selectors/user.selectors';
import {
  FORGOT_PASSWORD_PROCESS,
  FORGOT_USERNAME_PROCESS,
  REGISTER_USER_PROCESS,
  REMOVE_USER_PROCESS,
  StateWithUser,
  UPDATE_CREDENTIALS_PROCESS,
  UPDATE_PASSWORD_PROCESS,
  UPDATE_USER_DETAILS_PROCESS,
} from '../store/user-state';

@Injectable()
export class UserService implements UserFacade {
  constructor(
    private store: Store<StateWithUser & StateWithProcess<any>>,
    private userIdService: UserIdService,
    private connector: UserProfileConnector
  ) {}

  /**
   * Returns a user
   */
  get(): Observable<User> {
    return this.store.pipe(
      select(UserDetailsSelectors.getUserDetailsLoaderState),
      map((detailsLoaderState) => detailsLoaderState.value)
    );
  }

  isAdmin(): Observable<boolean> {
    return this.hasRole([B2BUserRole.ADMIN]);
  }

  isApprovalAdmin(): Observable<boolean> {
    return this.hasRole([B2BUserRole.ADMIN]);
  }

  isApprover(): Observable<boolean> {
    return this.hasRole([B2BUserRole.APPROVER]);
  }

  isApproverOrAdmin(): Observable<boolean> {
    return this.hasRole([B2BUserRole.ADMIN, B2BUserRole.APPROVER]);
  }

  isBuyer(): Observable<boolean> {
    return this.hasRole([B2BUserRole.CUSTOMER]);
  }

  isPunchOut(): Observable<boolean> {
    return this.get().pipe(map((u) => !!u && (u.customerType === CustomerType.OCI || u.customerType === CustomerType.CXML)));
  }

  isOci(): Observable<boolean> {
    return this.get().pipe(map((u) => !!u && u.customerType === CustomerType.OCI));
  }

  isCxml(): Observable<boolean> {
    return this.get().pipe(map((u) => !!u && u.customerType === CustomerType.CXML));
  }

  hasAddons(): Observable<boolean> {
    return this.get().pipe(
      map((user) => (user && user.hasAddons) ?? false),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  hasTendered(): Observable<boolean> {
    return this.get().pipe(
      map((user) => (user && user.hasTendered) ?? false),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  hasCustomerAssortment(): Observable<boolean> {
    return this.get().pipe(
      map((user) => (user && user.hasCustomerAssortment) ?? false),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  hasUniqueArticles(): Observable<boolean> {
    return this.get().pipe(
      map((user) => (user && user.hasUniqueArticles) ?? false),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /**
   * Register a new user
   *
   * @param submitFormData as UserRegisterFormData
   */
  register(userRegisterFormData: UserSignUp): void {
    this.store.dispatch(new UserActions.RegisterUser(userRegisterFormData));
  }

  getRegisterUserResult(): Observable<User | undefined> {
    return this.store.pipe(
      select(getProcessStateFactory(REGISTER_USER_PROCESS)),
      map((state: StateUtils.LoaderState<User>) => StateUtils.loaderValueSelector<User>(state))
    );
  }

  /**
   * Returns the register user process loading flag
   */
  getRegisterUserResultLoading(): Observable<boolean> {
    return this.store.pipe(select(getProcessLoadingFactory(REGISTER_USER_PROCESS)));
  }

  /**
   * Returns the register user process success flag
   */
  getRegisterUserResultSuccess(): Observable<boolean> {
    return this.store.pipe(select(getProcessSuccessFactory(REGISTER_USER_PROCESS)));
  }

  /**
   * Returns the register user process error flag
   */
  getRegisterUserResultError(): Observable<LoaderError | undefined> {
    return this.store.pipe(select(getProcessErrorFactory(REGISTER_USER_PROCESS)));
  }

  /**
   * Resets the register user process flags
   */
  resetRegisterUserProcessState(): void {
    return this.store.dispatch(new UserActions.ResetRegisterUserProcess());
  }

  /**
   * Remove user account, that's also called close user's account
   */
  remove(password): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.RemoveUser({ userId, password })),
      () => {}
    );
  }

  updateCredentials(credentials: Credentials): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.UpdateCredentials({ credentials, userId })),
      () => {}
    );
  }

  /**
   * Returns the credentials loading flag
   */
  getUpdateCredentialsResultLoading(): Observable<boolean> {
    return this.store.pipe(select(getProcessLoadingFactory(UPDATE_CREDENTIALS_PROCESS)));
  }

  /**
   * Returns the credentials failure outcome.
   */
  getUpdateCredentialsResultError(): Observable<LoaderError | undefined> {
    return this.store.pipe(select(getProcessErrorFactory(UPDATE_CREDENTIALS_PROCESS)));
  }

  /**
   * Returns the credentials process success outcome.
   */
  getUpdateCredentialsResultSuccess(): Observable<boolean> {
    return this.store.pipe(select(getProcessSuccessFactory(UPDATE_CREDENTIALS_PROCESS)));
  }

  resetUpdateCredentialsProcessingState(): void {
    this.store.dispatch(new UserActions.UpdateCredentialsReset());
  }

  /**
   * Returns the remove user loading flag
   */
  getRemoveUserResultLoading(): Observable<boolean> {
    return this.store.pipe(select(getProcessLoadingFactory(REMOVE_USER_PROCESS)));
  }

  /**
   * Returns the remove user failure outcome.
   */
  getRemoveUserResultError(): Observable<LoaderError | undefined> {
    return this.store.pipe(select(getProcessErrorFactory(REMOVE_USER_PROCESS)));
  }

  /**
   * Returns the remove user process success outcome.
   */
  getRemoveUserResultSuccess(): Observable<boolean> {
    return this.store.pipe(select(getProcessSuccessFactory(REMOVE_USER_PROCESS)));
  }

  /**
   * Resets the remove user process state. The state needs to be reset after the process
   * concludes, regardless if it's a success or an error
   */
  resetRemoveUserProcessState(): void {
    this.store.dispatch(new UserActions.RemoveUserReset());
  }

  /**
   * Return whether user's password is successfully reset
   */
  isPasswordReset(): Observable<boolean> {
    return this.store.pipe(select(ResetPasswordSelectors.getResetPassword) as any);
  }

  /**
   * Updates the user's details
   * @param userDetails to be updated
   */
  updatePersonalDetails(userDetails: User): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(
          new UserActions.UpdateUserDetails({
            username: userId,
            userDetails,
          })
        ),
      () => {}
    );
  }

  /**
   * Returns the update user's personal details loading flag
   */
  getUpdatePersonalDetailsResultLoading(): Observable<boolean> {
    return this.store.pipe(select(getProcessLoadingFactory(UPDATE_USER_DETAILS_PROCESS)));
  }

  /**
   * Returns the update user's personal details error flag
   */
  getUpdatePersonalDetailsResultError(): Observable<LoaderError | undefined> {
    return this.store.pipe(select(getProcessErrorFactory(UPDATE_USER_DETAILS_PROCESS)));
  }

  /**
   * Returns the update user's personal details success flag
   */
  getUpdatePersonalDetailsResultSuccess(): Observable<boolean> {
    return this.store.pipe(select(getProcessSuccessFactory(UPDATE_USER_DETAILS_PROCESS)));
  }

  /**
   * Resets the update user details processing state
   */
  resetUpdatePersonalDetailsProcessingState(): void {
    this.store.dispatch(new UserActions.ResetUpdateUserDetails());
  }

  /**
   * Reset new password.  Part of the forgot password flow.
   * @param token
   * @param password
   */
  resetPassword(token: string, password: string): void {
    this.store.dispatch(new UserActions.ResetPassword({ token, password }));
  }

  /*
   * Request an email to reset a forgotten password.
   */
  requestForgotPasswordEmail(email: string, username): void {
    this.store.dispatch(new UserActions.ForgotPasswordEmailRequest({ email, username }));
  }

  /**
   * Returns the update user's personal details loading flag
   */
  getRequestForgotPasswordEmailLoading(): Observable<boolean> {
    return this.store.pipe(select(getProcessLoadingFactory(FORGOT_PASSWORD_PROCESS)));
  }

  /**
   * Returns the update user's personal details error flag
   */
  getRequestForgotPasswordEmailError(): Observable<LoaderError | undefined> {
    return this.store.pipe(select(getProcessErrorFactory(FORGOT_PASSWORD_PROCESS)));
  }

  /**
   * Returns the update user's personal details success flag
   */
  getRequestForgotPasswordEmailSuccess(): Observable<boolean> {
    return this.store.pipe(select(getProcessSuccessFactory(FORGOT_PASSWORD_PROCESS)));
  }

  /**
   * Resets the update user details processing state
   */
  resetRequestForgotPasswordEmailProcessingState(): void {
    this.store.dispatch(new UserActions.ForgotPasswordEmailRequestReset());
  }

  /*
   * Request an email to reset a forgotten usename.
   */
  requestForgotUsernameEmail(email: string): void {
    this.store.dispatch(new UserActions.ForgotUsernameEmailRequest({ email }));
  }

  /**
   * Returns the update user's personal details loading flag
   */
  getRequestForgotUsernameEmailLoading(): Observable<boolean> {
    return this.store.pipe(select(getProcessLoadingFactory(FORGOT_USERNAME_PROCESS)));
  }

  /**
   * Returns the update user's personal details error flag
   */
  getRequestForgotUsernameEmailError(): Observable<LoaderError | undefined> {
    return this.store.pipe(select(getProcessErrorFactory(FORGOT_USERNAME_PROCESS)));
  }

  /**
   * Returns the update user's personal details success flag
   */
  getRequestForgotUsernameEmailSuccess(): Observable<boolean> {
    return this.store.pipe(select(getProcessSuccessFactory(FORGOT_USERNAME_PROCESS)));
  }

  /**
   * Resets the update user details processing state
   */
  resetRequestForgotUsernameEmailProcessingState(): void {
    this.store.dispatch(new UserActions.ForgotUsernameEmailRequestReset());
  }

  /**
   * Returns the update password loading flag
   */
  getUpdatePasswordResultLoading(): Observable<boolean> {
    return this.store.pipe(select(getProcessLoadingFactory(UPDATE_PASSWORD_PROCESS)));
  }

  /**
   * Returns the update password failure outcome.
   */
  getUpdatePasswordResultError(): Observable<LoaderError | undefined> {
    return this.store.pipe(select(getProcessErrorFactory(UPDATE_PASSWORD_PROCESS)));
  }

  /**
   * Returns the update password process success outcome.
   */
  getUpdatePasswordResultSuccess(): Observable<boolean> {
    return this.isPasswordReset().pipe(filter((isReset) => !!isReset));
  }

  /**
   * Resets the update password process state. The state needs to be reset after the process
   * concludes, regardless if it's a success or an error
   */
  resetUpdatePasswordProcessState(): void {
    this.store.dispatch(new UserActions.ResetPasswordReset());
  }

  private hasRole(roles: B2BUserRole[]): Observable<boolean> {
    return this.get().pipe(
      filter((user) => user && !!Object.keys(user).length),
      map((user) => {
        if (user && user.roles) {
          return user.roles;
        }
        return [];
      }),
      map((userRoles) => roles.some((role) => userRoles.indexOf(role) > -1)),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  validateUserEmail(email: string): Observable<UserInputCheck> {
    return this.userIdService.takeUserId().pipe(
      switchMap((userId) => {
        return this.connector.validateUserEmail(userId, email);
      })
    );
  }

  checkDuplicatedUsername(username: string): Observable<UserInputCheck> {
    return this.connector.checkDuplicatedUsername(username);
  }

  validateName(name: string): Observable<NameInputCheckResult> {
    return this.connector.validateName(name);
  }

  changeCustomerSessionLanguage(languageCode: string): void {
    this.userIdService
      .takeUserId()
      .pipe(take(1))
      .subscribe((userId) => this.store.dispatch(new UserActions.ChangeCustomerSessionLanguage({ userId, languageCode })));
  }
}
